import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Search, Building, Box, Phone, Loader2, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';
import { KIND, normalizePoints } from '../lib/deliveryPoints';

export default function DeliveryPoints() {
  const [facilityType, setFacilityType] = useState('ALL'); // ALL, PU, PAK
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deliveryData, setDeliveryData] = useState({ postOffices: [], lockers: [] });

  const fetchPoints = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Normalized through the same module the picker uses, so the code shown
      // on a card here is the code a label would carry.
      const data = await api.getDeliveryPoints(facilityType, searchText);
      setDeliveryData({
        postOffices: normalizePoints(data, KIND.POST_OFFICE),
        lockers: normalizePoints(data, KIND.LOCKER),
      });
    } catch (err) {
      setError(err.message);
      setDeliveryData({ postOffices: [], lockers: [] });
    } finally {
      setLoading(false);
    }
    // searchText is read on submit only, so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityType]);

  useEffect(() => {
    fetchPoints();
  }, [fetchPoints]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchPoints();
  };

  return (
    <div className="polaris-card">
      <div className="card-header">
        <div>
          <h2 className="card-title">Hrvatska Pošta Delivery Points & Parcel Lockers</h2>
          <p style={{ fontSize: '0.83rem', color: 'var(--color-secondary)', marginTop: '0.15rem' }}>
            Lookup Post Offices (Poštanski uredi) and Paketomati (Parcel Lockers) via HP DXWebAPI FetchParcelDeliveryPoint API.
          </p>
        </div>
      </div>

      <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)', background: '#fafafa' }}>
        <form onSubmit={handleSearchSubmit} className="toolbar-row">
          {/* Facility Type Selector */}
          <div style={{ minWidth: '180px' }}>
            <select className="form-control" value={facilityType} onChange={(e) => setFacilityType(e.target.value)}>
              <option value="ALL">All (Post Offices & Lockers)</option>
              <option value="PU">Post Offices Only (PU)</option>
              <option value="PAK">Parcel Lockers Only (Paketomat)</option>
            </select>
          </div>

          {/* Search text input */}
          <div className="toolbar-search">
            <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-secondary)' }} />
            <input
              type="text"
              className="form-control"
              style={{ paddingLeft: '2.3rem' }}
              placeholder="Search by Postal Code, City, or Street (e.g., Zagreb, Split, 10000)..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <Loader2 size={16} className="spin" /> : <Search size={16} />} Search
          </button>
        </form>
      </div>

      {error && (
        <div
          style={{
            margin: '1rem 1.5rem 0',
            background: '#fce8e6',
            color: '#c5221f',
            padding: '0.75rem 1rem',
            borderRadius: '6px',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div style={{ padding: '1.5rem' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Loader2 size={32} className="spin" style={{ color: 'var(--color-brand-hp)', marginBottom: '0.5rem' }} />
            <p style={{ color: 'var(--color-secondary)' }}>Searching Hrvatska Pošta delivery network...</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

            {/* Parcel Lockers (Paketomati) */}
            {(facilityType === 'ALL' || facilityType === 'PAK') && (
              <div>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Box size={18} color="#e20613" />
                  <span>Paketomati - Parcel Lockers ({deliveryData.lockers.length})</span>
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
                  {deliveryData.lockers.map((pak, i) => (
                    <div key={i} style={{ background: '#ffffff', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '1rem', boxShadow: 'var(--shadow-card)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <span className="badge badge-purple">Code: {pak.code}</span>
                        <span className="badge badge-green">24/7 Access</span>
                      </div>
                      <h4 style={{ fontSize: '0.92rem', fontWeight: 600, marginBottom: '0.3rem' }}>{pak.name}</h4>
                      <p style={{ fontSize: '0.83rem', color: 'var(--color-secondary)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <MapPin size={14} color="#e20613" /> {pak.address}, {pak.zip} {pak.city}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Post Offices (Poštanski uredi) */}
            {(facilityType === 'ALL' || facilityType === 'PU') && (
              <div>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Building size={18} color="#008060" />
                  <span>Poštanski Uredi - Post Offices ({deliveryData.postOffices.length})</span>
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
                  {deliveryData.postOffices.map((pu, i) => (
                    <div key={i} style={{ background: '#ffffff', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '1rem', boxShadow: 'var(--shadow-card)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <span className="badge badge-blue">PU {pu.code}</span>
                      </div>
                      <h4 style={{ fontSize: '0.92rem', fontWeight: 600, marginBottom: '0.3rem' }}>{pu.name}</h4>
                      <p style={{ fontSize: '0.83rem', color: 'var(--color-secondary)', display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '0.3rem' }}>
                        <MapPin size={14} color="#008060" /> {pu.address}, {pu.zip} {pu.city}
                      </p>
                      {pu.tel && (
                        <p style={{ fontSize: '0.8rem', color: 'var(--color-secondary)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          <Phone size={12} /> {pu.tel}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}
